import type {
  RoutePathOptionalParams,
  RoutePathRequiredParams,
} from './types/RouteParams';

export class RoutePath<
  TPrefix extends string,
  TPath extends string,
  TSearchParam extends string,
  TArraySearchParam extends string,
> {
  public readonly prefix: TPrefix;
  public readonly path: TPath;
  public readonly searchParams: TSearchParam[];
  public readonly arraySearchParams: TArraySearchParam[];

  constructor(data: {
    prefix: TPrefix;
    path: TPath;
    searchParams: TSearchParam[];
    arraySearchParams: TArraySearchParam[];
  }) {
    this.prefix = data.prefix;
    this.path = data.path;
    this.searchParams = data.searchParams;
    this.arraySearchParams = data.arraySearchParams;
  }

  get fullPath(): TPath extends '' ? TPrefix : `${TPrefix}/${TPath}` {
    if (this.path === '') {
      // @ts-expect-error The return type of `fullPath` is correct but there's no way to properly type-check this return statement
      return this.prefix;
    }

    // @ts-expect-error The return type of `fullPath` is correct but there's no way to properly type-check this return statement
    return `${this.prefix}/${this.path}`;
  }

  get params(): { type: 'required' | 'optional'; name: string }[] {
    return this.fullPath
      .split('/')
      .map((part) => {
        if (part === '*') return { type: 'required', name: '*' };

        if (part.startsWith(':')) {
          return part.endsWith('?')
            ? { type: 'optional', name: part.slice(1, -1) }
            : { type: 'required', name: part.slice(1) };
        }

        return null;
      })
      .filter(Boolean);
  }

  /**
   * Full path with params.
   *
   * The given params & searchParams are set to the url accordingly
   */
  with(
    params: Record<RoutePathRequiredParams<TPrefix | TPath>, string | number> &
      Partial<
        Record<RoutePathOptionalParams<TPrefix | TPath>, string | number>
      > &
      Partial<Record<TSearchParam, string | number | boolean>> &
      Partial<Record<TArraySearchParam, (string | number | boolean)[]>>,
  ) {
    // Url

    const pathname = this.params.reduce((currentPath: string, param) => {
      const value = params[param.name as keyof typeof params]?.toString() || '';

      // For optional parameters
      if (param.type === 'optional') {
        // Unless there's a value, remove the extra /
        if (value === '') return currentPath.replace(`/:${param.name}?`, '');
        return currentPath.replace(`:${param.name}?`, value);
      }

      return currentPath.replace(`:${param.name}`, value);
    }, this.fullPath);

    // Search

    const searchParamsObject = new URLSearchParams();

    const searchKeys = this.searchParams.filter(
      (key) => key in (params as Record<string, unknown>),
    );

    searchKeys.forEach((key) => {
      const value = params[key as string as keyof typeof params];

      if (typeof value === 'undefined' || value === null) return;

      if (Array.isArray(value)) {
        // Arrays should to on arraySearchParams
        // This is being kept here temporarily for backwards compatibility
        value.forEach((item) => searchParamsObject.append(key, String(item)));
      } else {
        searchParamsObject.append(key, String(value));
      }
    });

    const arraySearchParamsKeys = this.arraySearchParams.filter(
      (key) => key in (params as Record<string, unknown>),
    );

    arraySearchParamsKeys.forEach((key) => {
      const items = params[key as string as keyof typeof params];

      if (Array.isArray(items)) {
        items.forEach((value) => {
          if (typeof value !== 'undefined' && value !== null) {
            searchParamsObject.append(key, value as string);
          }
        });
      }
    });

    return searchParamsObject.size > 0
      ? `${pathname}${'?' + searchParamsObject.toString()}`
      : pathname;
  }

  /**
   * Returns the values from the URL, for the keys in this path's search params
   */
  searchParamsFromUrl(url: URL) {
    const result: Record<string, unknown> = {};

    for (const searchParam of this.searchParams) {
      if (url.searchParams.has(searchParam))
        result[searchParam] = url.searchParams.get(searchParam);
    }

    for (const arraySearchParam of this.arraySearchParams) {
      result[arraySearchParam] = url.searchParams.getAll(arraySearchParam);
    }

    return result as Partial<Record<TSearchParam, string>> &
      // "Required<Partial<" is a trick to get around TArraySearchParam being possibly never
      Required<Partial<Record<TArraySearchParam, string[]>>>;
  }
}
